import { Avatar, Box, ListItemAvatar, makeStyles, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Grid } from "@material-ui/core"
import { useEffect, useRef, useState, useCallback, useMemo } from "react"
import { Form, FormikProvider, useFormik } from "formik"
import FormField from "../../components/FormField"
import { useQueryClient } from "react-query"

import AttachmentIcon from "../../assets/icons/uploadAttachment.svg"
import EmojiIcon from "../../assets/icons/emoji.svg"
import ImagesUploadIcon from "../../assets/icons/uploadImages.svg"
import NCSendMessage from "../../shared/NCSendMessage"
import { usePubNub } from "pubnub-react"
import FileSaver from "file-saver"
import { DownloadIcon, DeleteIcon } from "../../libs/icons"
import EmojiPicker from "emoji-picker-react"
import {
  createDirectChannel,
  downloadUploadedFile,
  publishMessage,
  showTime
} from "../../libs/pubnub"
import { NOAH_CARES, STORAGE } from "../../libs/storage"
import NCText from "../../shared/NCText"
import NCAutocomplete from "../../shared/NCAutocomplete"
import NCModal from "../../shared/NCModal"
import NCButton from "../../shared/NCButton"
import WebCall from "./web-call"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { useGetThreadMessages, useGetChatParticipants } from "../../services/queries"
import { useSendMessage } from "../../services/mutations"
import API from "../../libs/api"
import { API_PATH, USER_TYPE } from "../../libs/config"
import { toast } from "react-toastify"

const useStyles = makeStyles(theme => ({
  chatContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
  },
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    padding: "10px 20px",
  },
  messageInputWrapper: {
    flexShrink: 0,
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#fff",
    padding: "12px 0",
  },
  sendText: {
    backgroundColor: "rgba(30, 170, 196, 1)",
    fontSize: "15px",
    color: "white",
    padding: "8px 14px",
    borderRadius: "16px 4px 16px 16px",
    display: "inline-block",
    wordBreak: "break-word",
  },
  receiveText: {
    backgroundColor: "#F3F4F6",
    fontSize: "15px",
    color: "#1F2937",
    padding: "8px 14px",
    borderRadius: "4px 16px 16px 16px",
    display: "inline-block",
    wordBreak: "break-word",
  },
  name: {
    fontSize: "32px",
    fontWeight: 700,
    color: "rgba(16, 101, 142, 1)",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word"
  },
  heading: {
    backgroundColor: "white",
    minHeight: "60px",
    padding: "4px 20px",
    borderBottom: "1px solid #E5E7EB",
    [theme.breakpoints.down("xs")]: {
      padding: "10px 8px",
      marginBottom: "5px"
    }
  },
  messageInputContainer: {
    display: "flex",
    alignItems: "center"
  },
  messageInput: {
    flex: 1,
    margin: "0px 10px",
    [theme.breakpoints.down("xs")]: {
      paddingTop: "5px",
      paddingBottom: "5px"
    }
  },
  icons: {
    padding: "10px 10px",
    cursor: "pointer",
    width: "24px",
    height: "24px"
  },
  messageBox: {
    width: "100%"
  },
  inputFile: {
    width: "0px",
    height: "0px",
    visibility: "none"
  },
  download: {
    cursor: "pointer",
    display: "flex",
    gap: "10px"
  },
  image: {
    maxWidth: "400px",
    maxHeight: "400px,",
    [theme.breakpoints.down("md")]: {
      maxWidth: "200px",
      maxHeight: "200px,"
    }
  },
  fileName: {
    wordBreak: "break-word"
  },
  emojipicker: {
    transform: "scale(0.7)"
  },
  emojiContainer: {
    position: "absolute",
    zIndex: 100,
    bottom: "58px",
    right: "42px"
  },
  avatar: {
    [theme.breakpoints.down("xs")]: {
      minWidth: "42px"
    }
  },
  messageDateTimePub: {
    fontSize: "12px",
    marginBottom: "5px",
    textAlign: "right",
    marginRight: "10px",
    color: "#777"
  },
  messageDateTime: {
    fontSize: "12px",
    marginBottom: "5px",
    textAlign: "left",
    marginLeft: "10px",
    color: "#777"
  }
}))

const UserMessaging = () => {
  const cls = useStyles()

  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const thread = location.state?.thread

  // Derive selectedUser — for single chats, it's the other participant; for groups, use thread name
  const currentUserId = STORAGE.getItem(NOAH_CARES)?.user?.id
  const isGroup = thread?.thread_type === 2
  const selectedUser = isGroup
    ? { name: thread?.name || "Group", id: thread?.id, uuid: thread?.pubnub_channel_id || String(thread?.id), avatar: "" }
    : thread?.participants?.find(p => p.user !== currentUserId) || thread?.participants?.[0] || {}

  const TEXT = "text"
  const DOCUMENT = "document"
  const IMAGE = "image"

  const [message, setMessage] = useState("")
  const [uploadType, setUploadType] = useState(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState({ open: false, file: null, type: null, messageText: null })
  const [realtimeMessages, setRealtimeMessages] = useState([])
  const [manageMembersOpen, setManageMembersOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [localParticipants, setLocalParticipants] = useState(thread?.participants || [])

  // Sync local participants when thread changes
  useEffect(() => {
    setLocalParticipants(thread?.participants || [])
  }, [thread?.id])

  // `thread` comes from router state — a snapshot taken when the chat was
  // opened, which goes stale after members are added/removed and the user
  // navigates away and back. Reload members from the server on open.
  const openManageMembers = async () => {
    setManageMembersOpen(true)
    if (!thread?.id) return
    try {
      const res = await API.get(`${API_PATH.THREADS}${thread.id}/`)
      const participants = (res?.data?.data || res?.data)?.participants
      if (Array.isArray(participants)) setLocalParticipants(participants)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to load members:", e)
    }
  }

  // Clear realtime messages when switching threads
  useEffect(() => {
    setRealtimeMessages([])
  }, [thread?.id])

  const queryClient = useQueryClient()

  const pubnub = usePubNub()
  const uuid = pubnub.getUUID()
  const inputRef = useRef()
  const inputImageRef = useRef()
  const messageListRef = useRef()

  // Load message history from our backend
  const { data: backendMessages, isLoading: isLoadingHistory } = useGetThreadMessages(
    thread?.id,
    { enabled: !!thread?.id }
  )

  // Mark thread as read when entering chat
  useEffect(() => {
    if (!thread?.id) return
    API.post(`${API_PATH.THREADS}${thread.id}/mark-read/`).catch(() => {})
  }, [thread?.id])

  // Send message mutation
  const sendMessageMutation = useSendMessage(thread?.id)

  // Prevent double-send when the user clicks Send multiple times quickly
  const [isSending, setIsSending] = useState(false)
  const sendingRef = useRef(false)

  // Group member management — follows add group pattern
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState("")
  const memberDebounceRef = useRef(null)
  const { data: chatParticipants } = useGetChatParticipants(debouncedMemberSearch)
  const memberOptions = useMemo(() =>
    (chatParticipants?.data?.data?.results || []).map(u => ({
      id: u.id,
      name: `${u.name || u.email} (${USER_TYPE[u.user_type] || "User"})`,
    })).filter(u => !localParticipants?.some(p => p.user === u.id)),
    [chatParticipants, localParticipants]
  )

  // Debounced server-side search for add-member autocomplete
  const handleMemberSearch = useCallback(value => {
    if (memberDebounceRef.current) clearTimeout(memberDebounceRef.current)
    memberDebounceRef.current = setTimeout(() => {
      setDebouncedMemberSearch(value)
    }, 400)
  }, [])

  const addMemberFormik = useFormik({
    initialValues: { new_member: null },
    onSubmit: async (values, { resetForm }) => {
      if (!values.new_member || !thread?.id) return
      try {
        await API.post(`${API_PATH.THREADS}${thread.id}/add-participant/`, { user_id: values.new_member })
        const addedUser = memberOptions.find(u => u.id === values.new_member)
        if (addedUser) {
          setLocalParticipants(prev => [...prev, { user: addedUser.id, name: addedUser.name }])
        }
        queryClient.invalidateQueries({ queryKey: ["threadMessages", thread.id] })
        queryClient.invalidateQueries({ queryKey: ["threads"] })
        resetForm()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to add member:", e)
      }
    },
  })

  const membersModalContent = (
    <FormikProvider value={addMemberFormik}>
      <Form onSubmit={addMemberFormik.handleSubmit}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <FormField
              as={NCAutocomplete}
              options={memberOptions}
              label="Add member"
              name="new_member"
              onChange={v => addMemberFormik.setFieldValue("new_member", v)}
              onSearch={handleMemberSearch}
            />
          </Grid>
          <Grid item xs={12} style={{ marginTop: 16 }}>
            {(localParticipants || []).map(p => (
              <Box key={p.id || p.user} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <NCText style={{ fontSize: 14 }}>
                  {p.name || `User ${p.user}`}
                  {p.user === thread?.created_by && (
                    <span style={{ fontSize: 11, color: "#9CA3AF", marginLeft: 6 }}>Owner</span>
                  )}
                </NCText>
                {p.user !== currentUserId && (
                  <IconButton size="small" onClick={() => handleRemoveMember(p.user)}>
                    <span style={{ color: "#e74c3c", fontSize: 16 }}>✕</span>
                  </IconButton>
                )}
              </Box>
            ))}
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  )

  const handleRemoveMember = async (userId) => {
    if (!thread?.id) return
    try {
      await API.post(`${API_PATH.THREADS}${thread.id}/remove-participant/`, { user_id: userId })
      setLocalParticipants(prev => prev.filter(p => p.user !== userId))
      queryClient.invalidateQueries({ queryKey: ["threadMessages", thread.id] })
      queryClient.invalidateQueries({ queryKey: ["threads"] })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to remove member:", e)
    }
  }

  const handleDeleteThread = async () => {
    if (!thread?.id) return
    setDeleting(true)
    try {
      await API.delete(`${API_PATH.THREADS}${thread.id}/`)
      queryClient.invalidateQueries({ queryKey: ["threads"] })
      toast.success("Group chat deleted")
      setDeleteDialogOpen(false)
      navigate("/direct-message")
    } catch (e) {
      toast.error("Failed to delete group chat")
    } finally {
      setDeleting(false)
    }
  }

  // Parse backend messages into display format
  const parsedBackendMessages = useCallback(() => {
    const raw = backendMessages?.data?.data || backendMessages?.data || []
    const list = Array.isArray(raw) ? raw : (raw?.results || [])
    return list.map(msg => ({
      id: msg.id,
      text: msg.text,
      sender_id: msg.sender_id,
      sender_name: msg.sender__name || msg.sender_name,
      createdAt: msg.created,
      file_url: msg.file_url,
      file_name: msg.file_name,
      file_type: msg.file_type,
    }))
  }, [backendMessages])

  // Merge backend history + real-time PubNub messages, deduplicating
  const backendMsgs = parsedBackendMessages()
  const allMessages = useMemo(() => {
    const uniqueRealtime = realtimeMessages.filter(m => {
      return !backendMsgs.some(b =>
        b.text === m.text &&
        String(b.sender_id) === String(m.sender_id) &&
        Math.abs(new Date(b.createdAt) - new Date(m.createdAt)) < 5000
      )
    })
    return [...backendMsgs, ...uniqueRealtime]
  }, [backendMsgs, realtimeMessages])

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight
    }
  }, [allMessages.length])

  // PubNub real-time listener
  useEffect(() => {
    if (!thread?.id) return
    const listener = {
      message: msgEvent => {
        // eslint-disable-next-line no-console
        console.log("[msg-detail] PubNub message received:", msgEvent?.channel, msgEvent?.message?.text)
        const msg = msgEvent.message
        // Skip own messages — we optimistically add them in handleConfirmSend
        const senderId = String(msg.user?._id || msg.publisher || "")
        if (senderId === String(currentUserId)) return
        setRealtimeMessages(prev => [...prev, {
          id: `rt-${Date.now()}`,
          text: msg.text || msg.message?.text,
          sender_id: msg.user?._id || msg.publisher,
          sender_name: msg.user?.name,
          createdAt: msg.createdAt || new Date().toISOString(),
          file_url: msg.image || msg.document || msg.file?.url,
          file_name: msg.fileName || msg.file?.name,
          file_type: msg.image ? "image" : msg.document ? "document" : "text",
        }])
        // Mark thread as read since user is viewing it
        API.post(`${API_PATH.THREADS}${thread.id}/mark-read/`).catch(() => {})
      },
    }
    pubnub.addListener(listener)
    const channel = thread?.pubnub_channel_id || thread?.id
    // eslint-disable-next-line no-console
    console.log("[msg-detail] subscribing to channel:", channel)
    pubnub.subscribe({ channels: [channel] })
    return () => {
      pubnub.removeListener(listener)
      pubnub.unsubscribe({ channels: [channel] })
    }
  }, [thread?.id])

  const downloadFile = async (channel, fileUrl, name) => {
    // If it's our own URL, download directly
    if (fileUrl && fileUrl.startsWith("http")) {
      FileSaver.saveAs(fileUrl, name)
      return
    }
    // Otherwise use PubNub download
    try {
      const file = await downloadUploadedFile(pubnub, { channel, id: fileUrl, name })
      FileSaver.saveAs(file.data)
    } catch {
      // Fallback: try direct download
      if (fileUrl) FileSaver.saveAs(fileUrl, name)
    }
  }

  const onEmojiClick = event => {
    setMessage(message + event.emoji)
    setShowEmojiPicker(false)
  }

  const handleChange = e => {
    const value = e.target.value
    setMessage(value)
  }

  const handleSendMessageOnEnter = e => {
    if (e.key === "Enter") {
      handleSendMessage()
    }
  }

  // Send a text message immediately — no confirmation dialog.
  const sendTextMessage = async (text) => {
    const currentUserData = STORAGE.getItem(NOAH_CARES).user
    const currentChannelId = thread?.pubnub_channel_id || selectedUser?.id
    const currentThreadId = thread?.id

    try {
      // Save to backend FIRST, then publish to PubNub
      try {
        await sendMessageMutation.mutateAsync({ text })
      } catch (e) {
        const status = e?.response?.status
        // 404/403 → the user was removed from this group thread, so leave and refresh
        if (status === 404 || status === 403) {
          toast.error("You have been removed from this group.")
          queryClient.invalidateQueries({ queryKey: ["threads"] })
          navigate("/direct-message")
          return
        }
        toast.error(e?.response?.data?.message || "Failed to send message")
      }

      // Publish to PubNub for real-time
      const msgPayload = {
        text,
        createdAt: new Date().toISOString(),
        user: { _id: currentUserData.id, name: currentUserData.name },
        type: TEXT,
      }
      // eslint-disable-next-line no-console
      console.log("[msg] publishing to channel:", currentChannelId, msgPayload)
      await publishMessage(pubnub, msgPayload, currentChannelId, {}, uuid)

      // Optimistically add own message (PubNub doesn't echo back to sender)
      setRealtimeMessages(prev => [...prev, {
        id: `sent-${Date.now()}`,
        text,
        sender_id: currentUserData.id,
        sender_name: currentUserData.name,
        createdAt: new Date().toISOString(),
        file_url: null,
        file_name: null,
        file_type: "text",
      }])

      // Optimistically update threads list cache (left panel) — no network call
      queryClient.setQueryData(["threads"], (old) => {
        if (!old?.data?.data?.results) return old
        return {
          ...old,
          data: {
            ...old.data,
            data: {
              ...old.data.data,
              results: old.data.data.results.map(t =>
                t.id === currentThreadId
                  ? { ...t, last_message: { text, created: new Date().toISOString() } }
                  : t
              )
            }
          }
        }
      })

      // Refetch backend messages to sync with server
      queryClient.invalidateQueries({ queryKey: ["threadMessages", thread.id] })

      // Update channel metadata (1-on-1 only)
      if (!isGroup) {
        const customData = generateCustomData(text)
        await createDirectChannel(pubnub, uuid, selectedUser?.uuid, customData)
      }

      setMessage("")
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error sending message:", error)
    }
  }

  const handleSendMessage = async () => {
    if (!message || sendingRef.current) {
      return
    }
    sendingRef.current = true
    setIsSending(true)
    try {
      // Text messages send immediately — no confirmation dialog.
      await sendTextMessage(message)
    } finally {
      sendingRef.current = false
      setIsSending(false)
    }
  }

  const generateCustomData = lastMessage => {
    const userData = STORAGE.getItem(NOAH_CARES).user
    const updatedAt = new Date()
    const message = {
      text: lastMessage,
      user: userData,
      _id: new Date().toString(),
      createdAt: updatedAt.toString()
    }
    const customData = {
      custom: {
        [uuid]: getCustomData(
          userData.name,
          userData.email,
          userData.avatar,
          updatedAt,
          message
        ),
        [selectedUser?.uuid]: getCustomData(
          selectedUser.name,
          selectedUser.email,
          selectedUser.avatar,
          updatedAt,
          message
        )
      }
    }
    return customData
  }

  const getCustomData = (name, email, avatar, updatedAt, lastMessage) => {
    return JSON.stringify({
      name: name || "",
      email: email || "",
      avatar: avatar || "",
      updatedAt: updatedAt || "",
      lastMessage
    })
  }

  const sendFile = async e => {
    const file = e?.target?.files?.[0]
    if (file) {
      setConfirmDialog({ open: true, file: file, type: "file", messageText: null })
    }
  }

  // File uploads go through a confirmation dialog first (upload is irreversible).
  const handleConfirmSend = async () => {
    // Close dialog immediately for responsive UX
    const { type, file } = confirmDialog
    const currentUploadType = uploadType
    const currentUserData = STORAGE.getItem(NOAH_CARES).user
    const currentChannelId = thread?.pubnub_channel_id || selectedUser?.id
    const currentThreadId = thread?.id

    setConfirmDialog({ open: false, file: null, type: null, messageText: null })

    try {
      if (type === "file" && file) {
        // Upload to our server first
        const formData = new FormData()
        formData.append("file", file)
        formData.append("text", "")
        try {
          const uploadRes = await API.post(
            `${API_PATH.THREADS}${currentThreadId}/upload/`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          )
          const fileUrl = uploadRes?.data?.data?.file_url || uploadRes?.data?.file_url || ""

          // Publish to PubNub with our file URL
          if (fileUrl) {
            const fileMsgPayload = {
              text: "",
              createdAt: new Date().toISOString(),
              user: { _id: currentUserData.id, name: currentUserData.name },
              type: currentUploadType,
              [currentUploadType]: fileUrl,
              fileName: file.name,
            }
            await publishMessage(pubnub, fileMsgPayload, currentChannelId, {}, uuid)
          }
        } catch (uploadErr) {
          // eslint-disable-next-line no-console
          console.error("File upload to server failed:", uploadErr)
        }

        // Reset file input
        if (currentUploadType === "document") {
          if (inputRef.current) inputRef.current.value = ""
        } else {
          if (inputImageRef.current) inputImageRef.current.value = ""
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error sending file:", error)
    }
  }

  const handleCancelSend = () => {
    // Reset file input if it's a file
    if (confirmDialog.file) {
      if (uploadType === "document" && inputRef.current) {
        inputRef.current.value = ""
      } else if (inputImageRef.current) {
        inputImageRef.current.value = ""
      }
    }
    setConfirmDialog({ open: false, file: null, type: null, messageText: null })
  }

  useEffect(() => {
    if (selectedUser?.uuid && thread?.id) {
      const channelId = thread?.pubnub_channel_id || selectedUser?.id
      pubnub.subscribe({ channels: [channelId] })
    }
  }, [selectedUser?.uuid, thread?.id])

  const messageRenderer = msg => {
    const senderId = msg.sender_id
    const isMe = String(senderId) === String(STORAGE.getUserDetails()?.id)
    return (
      <Box
        display={"flex"}
        style={{ width: "100%", marginBottom: 12 }}
        alignItems={"flex-start"}
        key={msg.id}
      >
        {!isMe && (
          <Avatar src={selectedUser?.avatar} style={{ width: 28, height: 28, marginRight: 8, marginTop: 16, flexShrink: 0 }} />
        )}
        <Box style={{ maxWidth: "70%", display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start", marginLeft: isMe ? "auto" : 0, marginRight: isMe ? 0 : "auto" }}>
        {msg.file_type === DOCUMENT && (
          <div>
            {!isMe && msg.sender_name && (
              <NCText style={{ fontSize: 12, color: "#6B7280", marginBottom: 2 }}>
                {msg.sender_name}
              </NCText>
            )}
            <span
              onClick={() => {
                if (msg.file_url) {
                  downloadFile(thread?.id, msg.file_url, msg.file_name)
                }
              }}
              className={isMe ? [cls.sendText, cls.download].join(" ") : [cls.receiveText, cls.download].join(" ")}
            >
            <span className={cls.fileName}>{msg.file_name || "File"}</span>
            <DownloadIcon />
          </span>
          </div>
        )}
        {msg.file_type === IMAGE && (
          <div>
            {!isMe && msg.sender_name && (
              <NCText style={{ fontSize: 12, color: "#6B7280", marginBottom: 2 }}>
                {msg.sender_name}
              </NCText>
            )}
            <span
              style={{ backgroundColor: "transparent" }}
              onClick={() => { if (msg.file_url) window.open(msg.file_url, "_blank") }}
              className={isMe ? [cls.sendText, cls.download].join(" ") : [cls.receiveText, cls.download].join(" ")}
            >
              <img src={msg.file_url} className={cls.image} alt="" />
            </span>
          </div>
        )}
        {(!msg.file_type || msg.file_type === "text") && msg.text && (
          <>
            {!isMe && msg.sender_name && (
              <NCText style={{ fontSize: 11, color: "#6B7280", marginBottom: 2 }}>
                {msg.sender_name}
              </NCText>
            )}
            <NCText className={isMe ? cls.sendText : cls.receiveText} style={{ marginBottom: 2 }}>
              {msg.text}
            </NCText>
            <NCText style={{ fontSize: 10, color: "#9CA3AF" }}>
              {showTime(msg.createdAt)}
            </NCText>
          </>
        )}
        </Box>
      </Box>
    )
  }

  return (
    <div className={cls.chatContainer}>
      <div className={cls.emojiContainer}>
        {showEmojiPicker && <EmojiPicker onEmojiClick={onEmojiClick} />}
      </div>
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        className={cls.heading}
      >
        <Box display={"flex"} alignItems={"center"} style={{ overflow: "hidden", flex: 1, minWidth: 0 }}>
          <ListItemAvatar className={cls.avatar}>
            <Avatar src={selectedUser?.avatar}></Avatar>
          </ListItemAvatar>
          <NCText style={{ overflow: "hidden", minWidth: 0, display: "flex", alignItems: "baseline", flexWrap: "wrap" }}>
            <span className={cls.name} style={{ fontSize: 16 }}>
              {selectedUser?.name || ""}
            </span>
          </NCText>
        </Box>
        <Box style={{ flexShrink: 0, marginLeft: 8, display: "flex", alignItems: "center", gap: 8 }}>
          {isGroup && (
            <>
              <IconButton size="small" onClick={openManageMembers} title="Manage members">
                <span style={{ fontSize: 18 }}>👥</span>
              </IconButton>
              <IconButton size="small" onClick={() => setDeleteDialogOpen(true)} title="Delete group">
                <DeleteIcon style={{ width: 18, height: 18, fill: "#E22134" }} />
              </IconButton>
            </>
          )}
          {!isGroup && <WebCall uuid={selectedUser.uuid} />}
        </Box>
      </Box>
      <div ref={messageListRef} className={cls.messagesContainer}>
        {isLoadingHistory && <NCText>Loading messages...</NCText>}
        {allMessages.map(msg => messageRenderer(msg))}
      </div>

      <div className={cls.messageInputWrapper}>
        <div className={cls.messageInputContainer}>
          <div className={cls.messageInput}>
            <NCSendMessage
              rows={3}
              className={cls.messageBox}
              onKeyUp={handleSendMessageOnEnter}
              placeholder={t("chat.type_message_here")}
              onSend={handleSendMessage}
              onChange={handleChange}
              value={message}
              disabled={isSending}
            />
          </div>
          <div>
            <input
              ref={inputRef}
              type="file"
              className={cls.inputFile}
              onChange={sendFile}
            />
            <input
              ref={inputImageRef}
              type="file"
              accept="image/*"
              className={cls.inputFile}
              onChange={sendFile}
            />
            <img
              src={AttachmentIcon}
              className={cls.icons}
              onClick={() => {
                setUploadType(DOCUMENT)
                inputRef.current.click()
              }}
            />
            <img
              src={ImagesUploadIcon}
              className={cls.icons}
              onClick={() => {
                setUploadType(IMAGE)
                inputImageRef.current.click()
              }}
            />
            <img
              src={EmojiIcon}
              className={cls.icons}
              onClick={() => {
                setShowEmojiPicker(picker => !picker)
              }}
            />
          </div>
        </div>
      </div>

      <Dialog open={confirmDialog.open} onClose={handleCancelSend} PaperProps={{ style: { fontFamily: "'DM Sans', Roboto, Helvetica, Arial, sans-serif" } }}>
        <DialogTitle>Confirm Send File</DialogTitle>
        <DialogContent>
          Send file: <strong>{confirmDialog.file?.name?.split("/").pop()}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSend} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmSend} color="primary" variant="contained">
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Member Management Modal — same pattern as New Group */}
      <NCModal
        open={manageMembersOpen}
        title={`Manage Members — ${selectedUser?.name}`}
        text={membersModalContent}
        onClose={() => { setManageMembersOpen(false); addMemberFormik.resetForm() }}
        submitButton={
          <NCButton
            variant="contained"
            color="primary"
            disableElevation
            onClick={() => addMemberFormik.submitForm()}
            style={{ width: "auto" }}
          >
            Add
          </NCButton>
        }
      />

      {/* Delete Group Confirmation Modal */}
      <NCModal
        open={deleteDialogOpen}
        title="Delete Group Chat"
        text={`Are you sure you want to delete "${selectedUser?.name}"? This action cannot be undone.`}
        onClose={() => setDeleteDialogOpen(false)}
        submitButton={
          <NCButton
            variant="contained"
            disableElevation
            disabled={deleting}
            onClick={handleDeleteThread}
            style={{ width: "auto", backgroundColor: "#E22134", color: "#fff" }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </NCButton>
        }
      />

    </div>
  )
}

export default UserMessaging
